import { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Header,
  Content,
  Table,
  TableColumn,
  Progress,
} from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@material-ui/core';

// Lista/inclui/remove os domínios onde os apps podem ser publicados. Lê e
// grava via backend (packages/backend/src/modules/domainsPlugin.ts), que
// guarda tudo em domains.json no bucket MinIO. Cada linha é (subdomínio,
// domínio): "*" vale pra qualquer app (<app>.<domínio>), um nome vira um
// endereço fixo (<nome>.<domínio>), "@" é o próprio domínio, sem subdomínio.
type DomainRow = {
  subdomain: string;
  domain: string;
  // Hosts dos apps já publicados nesta linha (undefined = não deu pra consultar o cluster).
  apps?: string[];
};

const hostOf = (row: DomainRow) =>
  row.subdomain === '@' ? row.domain : `${row.subdomain}.${row.domain}`;

export const DomainsPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [domains, setDomains] = useState<DomainRow[] | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createSubdomain, setCreateSubdomain] = useState('*');
  const [createDomain, setCreateDomain] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<DomainRow | undefined>();

  const loadDomains = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('domains');
      const resp = await fetchApi.fetch(`${baseUrl}/domains`);
      if (resp.status === 403) {
        throw new Error('Só quem está no grupo "admin" pode ver esta lista.');
      }
      if (!resp.ok) {
        throw new Error(`Falha ao carregar domínios (${resp.status})`);
      }
      const data = (await resp.json()) as { domains: DomainRow[] };
      setDomains(data.domains);
      setErrorMessage(undefined);
    } catch (e) {
      setErrorMessage((e as Error).message);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('domains');
      const resp = await fetchApi.fetch(`${baseUrl}/domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subdomain: createSubdomain, domain: createDomain }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}) as { error?: string });
        throw new Error(body.error ?? `Falha ao incluir domínio (${resp.status})`);
      }
      setCreateOpen(false);
      setCreateSubdomain('*');
      setCreateDomain('');
      await loadDomains();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('domains');
      const qs = new URLSearchParams({
        subdomain: deleteTarget.subdomain,
        domain: deleteTarget.domain,
      });
      const resp = await fetchApi.fetch(`${baseUrl}/domains?${qs}`, {
        method: 'DELETE',
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}) as { error?: string });
        throw new Error(body.error ?? `Falha ao remover domínio (${resp.status})`);
      }
      setDeleteTarget(undefined);
      await loadDomains();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const columns: TableColumn<DomainRow>[] = [
    { title: 'Subdomínio', field: 'subdomain' },
    { title: 'Domínio', field: 'domain' },
    {
      title: 'Endereço dos apps',
      render: row =>
        row.subdomain === '*'
          ? `<nome-do-app>.${row.domain}`
          : hostOf(row),
    },
    {
      title: 'Apps usando',
      customSort: (a, b) => (a.apps?.length ?? -1) - (b.apps?.length ?? -1),
      render: row => (row.apps ? row.apps.length : '-'),
    },
    {
      title: 'Ações',
      sorting: false,
      render: row => (
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          disabled={saving}
          onClick={() => setDeleteTarget(row)}
        >
          Remover
        </Button>
      ),
    },
  ];

  return (
    <Page themeId="tool">
      <Header
        title="Domínios"
        subtitle="Onde os apps podem ser publicados (usado na escolha do endereço ao criar uma aplicação)"
      />
      <Content>
        {errorMessage && <p>{errorMessage}</p>}
        {!domains && !errorMessage && <Progress />}
        <Button variant="contained" color="primary" onClick={() => setCreateOpen(true)}>
          Incluir domínio
        </Button>
        {domains && (
          <Table
            title={`${domains.length} domínio(s)`}
            columns={columns}
            data={domains}
            options={{ search: true, paging: domains.length > 20 }}
          />
        )}
      </Content>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Incluir domínio</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Subdomínio"
            helperText='"*" = qualquer app (<nome-do-app>.domínio). "@" = o próprio domínio, sem subdomínio (ex: empresa.com). Ou um nome fixo, ex: api (letras minúsculas, números e hífen)'
            value={createSubdomain}
            onChange={e => setCreateSubdomain(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Domínio"
            helperText="Ex: empresa.com ou empresa.com.br (o DNS precisa apontar para a VPS; para @ use um registro A, não CNAME)"
            value={createDomain}
            onChange={e => setCreateDomain(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button
            color="primary"
            disabled={!createSubdomain || !createDomain || saving}
            onClick={handleCreate}
          >
            Incluir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(undefined)}>
        <DialogTitle>Remover domínio</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remover <strong>{deleteTarget ? hostOf(deleteTarget) : ''}</strong> da
            lista? Apps já publicados nele continuam no ar, só deixa de ser uma
            opção para novos.
            {deleteTarget?.apps && deleteTarget.apps.length > 0 && (
              <>
                {' '}
                <strong>
                  Atenção: {deleteTarget.apps.length} app(s) usam este domínio hoje:
                </strong>{' '}
                {deleteTarget.apps.join(', ')}.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(undefined)}>Cancelar</Button>
          <Button color="secondary" disabled={saving} onClick={handleDelete}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};
